import { Loader2 } from "lucide-react";

import { HealthlinkXsltFrame } from "features/healthlink/components/HealthlinkXsltFrame/HealthlinkXsltFrame";
import { useHealthlinkMessageQuery } from "features/results/hooks/useHealthlinkMessageQuery";
import { useHealthlinkMessagesByType } from "features/results/hooks/useHealthlinkMessagesByType";

import styles from "./XmlXsltPreview.module.css";

/**
 * messageType: "lab" | "radiology" — used as a temporary XSL source.
 * queryMessages never returns the XSL, so when it is missing we fetch the
 * type-specific messages endpoint, which auto-downloads the stylesheet on
 * the BE and returns it with its results.
 */
export const XmlXsltPreview = ({ messageId, messageType, fallback = null }) => {
  const { data, isLoading } = useHealthlinkMessageQuery(messageId);
  const message = data?.results?.[0];

  const hasXml = !!message?.xml;
  const messageXsl = message?.xsl || null;

  const { data: typeMessages, isLoading: isLoadingTypeMessages } =
    useHealthlinkMessagesByType(messageType, {
      enabled: hasXml && !messageXsl,
    });

  const fallbackXsl =
    typeMessages?.results?.find((result) => !!result.xsl)?.xsl || null;

  const xsl = messageXsl || fallbackXsl;

  if (isLoading && messageId) {
    return (
      <div className={styles.loadingState}>
        <Loader2 className={styles.loadingSpinner} size={24} />
        <p>Loading message…</p>
      </div>
    );
  }

  // No XML for this result — fall back to the structured (legacy) view.
  if (!messageId || !hasXml) {
    return fallback;
  }

  if (!xsl && isLoadingTypeMessages) {
    return (
      <div className={styles.loadingState}>
        <Loader2 className={styles.loadingSpinner} size={24} />
        <p>Loading message…</p>
      </div>
    );
  }

  // Have XML but no stylesheet — fall back to the structured (legacy) view.
  if (!xsl) {
    return fallback;
  }

  return (
    <HealthlinkXsltFrame
      xml={message.xml}
      xsl={xsl}
      documentKind={message.messageType || message.resultType}
    />
  );
};
